# Route: /tracking/records/:recordId/sign
# Apply a counter-signature (visa) to the next pending signature slot on an entry.
# This is a GMP-style acknowledgement/countersign — the authenticated user confirms
# they have reviewed the entry. It is NOT a 21 CFR Part 11 regulated e-signature
# (which requires password re-entry + UserID + meaning field — see F45b backlog).
# Permission: TrackingRecord:sign
# Feature: F53a G7 (already-signed list — operator sees who has signed before them)
# Layout (top→bottom): the reviewer sees the entry information + narrative they are
# reviewing first, then the signatures already applied, then the action buttons.

page-tracking-record-sign:
  type: Page
  archetype: sign
  meta:
    title: $t.tracking.sign.meta_title
    route: /tracking/records/:recordId/sign
    layout: shell
    auth: required
  data:
    record:
      model: TrackingRecord
      key: $route.recordId
      required: true
    # Countersign payload. `userRole` is seeded at mount from the fetched
    # entry's next pending slot RoleRequired (the GET joins the template, so
    # nextSlotRoleRequired is populated). It is carried to the API by a hidden
    # input, not an editable control — the slot dictates the role and the
    # review queue only surfaces entries the signer is eligible to sign, so the
    # slot role re-passes the backend role re-check. SignTrackingRecordRequest
    # requires UserRole (empty → 400), so this field MUST be present in the body.
    form:
      model: SignRecordForm
      initial:
        userRole: $data.record.nextSlotRoleRequired
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - sign-header
    - sign-context-header
    - sign-record-summary
    - sign-already-signed
    - sign-action

# ── Header ───────────────────────────────────────────────────────────────────

sign-header:
  type: PageHeader
  content: $t.tracking.sign.title
  back_route: /tracking/records/:recordId

# F80 FR-3 — Asset · Tracking · Shift chips for the entry being signed.
sign-context-header:
  type: Widget
  ref: xp87-tracking-context-header
  params:
    context-type: $data.record.contextType
    context-display-name: $data.record.contextDisplayName
    tracking-name: $data.record.formName
    tracking-version: $data.record.formVersionNumber
    timestamp: $data.record.timestamp

# ── Entry information + narrative (what the reviewer is signing) ───────────────

sign-record-summary:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - sign-summary-form
    - sign-summary-context
    - sign-summary-text
    - sign-summary-structured

# F80 FR-6 — the reviewer signs the record as captured: structured values
# labeled per the entry's pinned template version. Same widget + props as
# record-detail-structured; its `record` param is bound to $data.record (this
# page fetches it) and it hides itself when the entry carries no values.
sign-summary-structured:
  type: Widget
  ref: xp87-tracking-structured-fields
  params:
    record: $data.record
    title: $t.tracking.detail.structured_title
    label-bool-true: $t.tracking.structured.bool_true
    label-bool-false: $t.tracking.structured.bool_false
    label-fetch-error: $t.tracking.structured.fetch_error
    label-orphan-note: $t.tracking.structured.orphan_note

sign-summary-form:
  type: Text
  bind:
    content: record.formName
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

sign-summary-context:
  type: Text
  bind:
    content: record.contextDisplayName
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface-muted

sign-summary-text:
  type: Text
  bind:
    content: record.text
  tokens:
    font_size: $font.size.md

# ── Signatures already applied ────────────────────────────────────────────────
# Enumerates signatures already applied on this entry so the signer can see
# who has signed before them, in what role, and when.

sign-already-signed:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - sign-already-signed-title
    - sign-already-signed-table

sign-already-signed-title:
  type: Text
  content: $t.tracking.sign.already_signed_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

sign-already-signed-table:
  type: DataTable
  bind:
    rows: record.signatures
  empty: sign-already-signed-empty
  columns:
    - key: slot
      label: $t.tracking.detail.col_sig_slot
      tokens:
        font_size: $font.size.sm
    - key: signedByDisplayName
      label: $t.tracking.detail.col_sig_user
      tokens:
        font_size: $font.size.sm
    - key: timestamp
      label: $t.tracking.detail.col_sig_timestamp
      format: compliance-datetime
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: meaning
      label: $t.tracking.detail.col_sig_meaning
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.tracking.sign.already_signed_title
  tokens:
    border: $border.default
    radius: $radius.md

sign-already-signed-empty:
  type: EmptyState
  content: $t.tracking.sign.already_signed_empty
  body: $t.tracking.sign.already_signed_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# ── Signature action ─────────────────────────────────────────────────────────
#
# IMPORTANT: this is a `type: Form` with `action: { type: invoke, operation:
# sign }`, NOT a bare FormActions. A bare FormActions dispatches nothing — it
# would show a "saved" toast while the entry stayed PendingReview and no
# signature was ever recorded (a regulated-integrity failure: the supervisor
# believes a countersignature exists when it does not). The Form routes through
# providers.data.trackingEntry_sign(id, data) → POST /api/tracking/records/{id}/sign
# (declared in providers/data-provider.cir.yaml `operations.sign`, body:
# passthrough). on_success navigation runs ONLY after the server returns 2xx
# (the Form gates navigation on xp85-action-complete success), so a 400/403/
# 409/422 rejection surfaces the server message and keeps the operator on-page.
#
# The submitted body is exactly { userRole: "<slot role>" } — the form collector
# walks the bound inputs and assembles top-level keys along each bind path. The
# hidden input (sign-field-role) carries form.userRole; the "Signing as:" line
# is read-only transparency for the role being applied.

sign-action:
  type: Form
  model: SignRecordForm
  a11y:
    label: $t.tracking.sign.title
  action:
    type: invoke
    model: TrackingRecord
    operation: sign
    # Explicit success toast — the generic "Saved" is wrong for a regulated
    # countersign. Overrides the emitter default via the success_message →
    # xp85-action-complete detail → router toast path (issue #205).
    success_message: $t.tracking.sign.countersigned_toast
    # on_success destination: the review queue. The just-signed entry drops off
    # the queue, giving the supervisor visual confirmation the signature landed.
    # DEFAULT pending navigation-UX decision (F75 A1) — trivial to change here.
    on_success: navigate(/tracking/reviews)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - sign-role-transparency
    - sign-field-role
    - sign-form-actions

# Read-only transparency: shows the role the supervisor is signing as. Not an
# editable control — the slot dictates the role. Canonical label/value pattern
# (mirrors record-detail's info grid): a static i18n label cell + a data-bound
# value cell. The submitted role comes from the hidden sign-field-role input,
# both seeded from the same record.nextSlotRoleRequired.
sign-role-transparency:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - sign-role-label
    - sign-role-value

sign-role-label:
  type: Text
  content: $t.tracking.sign.signing_as
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

sign-role-value:
  type: Text
  bind:
    content: record.nextSlotRoleRequired
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# Hidden payload-only field — carries the slot role into the POST body as the
# top-level `userRole` key the API requires. Seeded from record.nextSlotRoleRequired
# via data.form.initial; never shown or edited (mirrors record-create's hidden
# inputSource field).
sign-field-role:
  type: Input
  input_type: hidden
  bind: form.userRole

sign-form-actions:
  type: FormActions
  submit_label: $t.tracking.sign.btn_sign
  cancel_route: /tracking/records/:recordId
  cancel_params:
    recordId: $route.recordId
  enabled: record.status == 'PendingReview'
